import LedFeedback from '@components/common/Feedback/LedFeedback';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { AddHomeOutlined, HomeOutlined, Refresh } from '@mui/icons-material';
import React from 'react';
import styled from 'styled-components';
import Utils from '@utils/index';
import { API } from 'aws-amplify';
import { Aim } from './WebRTCPTZControl';
import { last } from 'lodash';
import { useKeyPress } from '@hooks/KeyPress/KeyPressHooks';
import { useMobileQuery } from '@hooks/useMobileQuery';

const DirectionContainer = styled.div`
  position: absolute;
  width: 7.5rem;
  height: 7.5rem;
  left: calc(50% - 3.75rem);
  bottom: calc(50% - 3.75rem);
  display: flex;
  flex-direction: column;

  background: rgba(50, 50, 50, 0.0);
  border-radius: 50%;
  border: 1px solid #FFFFFF;

  .MuiSvgIcon-root {
    visibility: hidden;
  }

  :hover {
    .MuiSvgIcon-root {
      visibility: visible;
    }

    background: rgba(50, 50, 50, 0.5);
  }

  svg {
    color: white;
  }

  -webkit-transition: background-color 200ms linear;
  -ms-transition: background-color 200ms linear;
  transition: background-color 200ms linear;

  z-index: 5;

  pointer-events: none;
`

const ArrowContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const ZoomContainer = styled.div`
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  display: grid;
  grid-template-columns: min-content min-content min-content;
  grid-gap: 1rem;
  align-items: center;

  svg {
    color: black;
  }

  .MuiButton-contained {
    background-color: rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.8) !important;

    min-width: 0 !important;
  }

  z-index: 5;
`

const AXIS_API_PATH = '/axis-cgi/com/ptz.cgi';
const AXIS_API_CONFIG_PATH = '/axis-cgi/com/ptzconfig.cgi'
const AXIS_MOVE = '?move=';
const AXIS_SET_CHOME = '&home=yes'
const AXIS_SET_PRESET = '?setserverpresetname=Home'
const CAMERA_INDEX = '&camera='

interface IPTZControlProps {
  kvsName?: string;
  siteId?: string;
  rportCameraId?: string;
  onExitRequest?: () => void;
  wraperStyles?: React.CSSProperties;
  ptz?: { mjpegUrl?: string, onvifPassword?: string, onvifUser?: string };
  isSuperAdministrator?: boolean;
}

const PTZControl: React.FC<IPTZControlProps> = (props) => {
  const {
    kvsName,
    wraperStyles,
    siteId,
    rportCameraId,
    ptz,
    isSuperAdministrator
  } = props;

  const [mjpegUrl, setMjpegUrl] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [host, setHost] = React.useState<string>("");
  const [commandFeedback, setCommandFeedback] = React.useState<boolean | undefined>(undefined);
  const [cameraIndex, setCameraIndex] = React.useState<number>(1);
  const isMobile = useMobileQuery();

  const pingIntervalRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const hostRef = React.useRef(host);
  const ptzRef = React.useRef(ptz);
  const cameraIndexRef = React.useRef(cameraIndex);
  const rportCameraIdRef = React.useRef(rportCameraId);
  const siteIdRef = React.useRef(siteId);
  const kvsNameRef = React.useRef(kvsName);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const centerDivRef = React.useRef<HTMLDivElement | null>(null);
  const currentZoomRef = React.useRef<number>(0);
  const allowZoom = React.useRef<boolean>(true);
  const zoomActionPool = React.useRef<{ time: number, value: number }[]>([])

  const perform = React.useCallback(async () => {
    try {
      setError(undefined);
      const token = await getToken();
      const url = await getExposedUrl(token, rportCameraIdRef.current || "");

      let camIndex = 1;
      let fps: number | undefined | string = 5;
      let resolution: string | undefined = "1280x720";

      if (ptzRef.current && ptzRef.current.mjpegUrl) {
        camIndex = Utils.PTZ.getSubstream(ptzRef.current.mjpegUrl);
        setCameraIndex(camIndex);

        if (ptzRef.current.mjpegUrl.includes('fps=')) {
          fps = ptzRef.current.mjpegUrl.split('fps=')[1].charAt(0);
        }

        if (ptzRef.current.mjpegUrl.includes('resolution=')) {
          resolution = ptzRef.current.mjpegUrl.split('resolution=')[1];
        }
      }

      setMjpegUrl(`${url.url}/axis-cgi/mjpg/video.cgi?camera=${camIndex}&fps=${fps}&resolution=${resolution}`)

      setHost(Utils.PTZ.getHost(url.url));

      pingIntervalRef.current = setInterval(() => {
        window.fetch(url.heartbeatUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      }, 6000)
    } catch (error: any) {
      setError(error.message);
      console.log(error);
    }
  }, [])

  React.useEffect(() => { siteIdRef.current = siteId }, [siteId]);
  React.useEffect(() => { kvsNameRef.current = kvsName }, [kvsName])
  React.useEffect(() => { ptzRef.current = ptz }, [ptz])
  React.useEffect(() => { hostRef.current = host }, [host]);
  React.useEffect(() => { cameraIndexRef.current = cameraIndex }, [cameraIndex]);
  React.useEffect(() => { rportCameraIdRef.current = rportCameraId }, [rportCameraId])
  React.useEffect(onMount, [kvsName, perform]);
  React.useEffect(unMount, []);

  useKeyPress('+', () => {
    const url = `${host}${AXIS_API_PATH}?rzoom=${100}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  useKeyPress('-', () => {
    const url = `${host}${AXIS_API_PATH}?rzoom=${-100}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  useKeyPress('ArrowUp', () => {
    const url = `${host}${AXIS_API_PATH}?rtilt=${1}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  useKeyPress('ArrowDown', () => {
    const url = `${host}${AXIS_API_PATH}?rtilt=${-1}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  useKeyPress('ArrowLeft', () => {
    const url = `${host}${AXIS_API_PATH}?rpan=${-1}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  useKeyPress('ArrowRight', () => {
    const url = `${host}${AXIS_API_PATH}?rpan=${1}${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }, [])

  function unMount() {
    return () => {
      clearInterval(pingIntervalRef.current);
    }
  }

  function onMount() {
    perform();
  }

  async function getToken(): Promise<string> {
    try {
      const res = await API.get('api-service', `/streams/${siteIdRef.current}/${kvsNameRef.current}/rport/authorize`, {})
      return res.data.token;
    } catch (error) {
      throw new Error(Utils.Error.getErrorMessage(error));
    }
  }

  async function getExposedUrl(token: string, cameraId: string): Promise<{ url: string, heartbeatUrl: string }> {
    try {
      const result = await window.fetch(`${process.env.REACT_APP_RPORT_WRAPER_API_URL}/cameras/${cameraId}/expose`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        method: 'PUT'
      })

      const json = await result.json();
      if (json.error) {
        if (json.status_code === 400 && json.error.includes('camera') && json.error.includes('exposed')) throw new Error('Camera is being controlled by someone else')
        else throw new Error(json.error)
      }

      return {
        url: json.data.exposed_url,
        heartbeatUrl: json.data.heartbeat_url
      };
    } catch (error: any) {
      console.log(error);
      throw new Error(error.message || "Failed to connect.");
    }
  }

  function home() {
    const url = `${host}${AXIS_API_PATH}${AXIS_MOVE}home${CAMERA_INDEX}${cameraIndex}`
    issueCommand(url);
  }

  function setCurrentPositionAsHome(): void {
    const url = `${host}${AXIS_API_CONFIG_PATH}${AXIS_SET_PRESET}${AXIS_SET_CHOME}${CAMERA_INDEX}${cameraIndex}`;
    issueCommand(url);
  }

  function issueCommand(url: string) {
    Utils.PTZ.issueCommand(url, (error: any) => {
      if (error) {
        setCommandFeedback(false);
        setTimeout(() => {
          setCommandFeedback(undefined);
        }, 500)
      } else {
        setCommandFeedback(true);
        setTimeout(() => {
          setCommandFeedback(undefined);
        }, 500)
      }
    });
  }

  const handleClickEvent = React.useCallback((event: React.MouseEvent | undefined, zoomFactor?: number) => {
    if (centerDivRef && centerDivRef.current && event && imageRef && imageRef.current) {
      const imageElement = imageRef.current;
      const imageBounds = imageElement.getBoundingClientRect();

      // Get the actual intrinsic resolution of the video
      const videoWidth = imageElement.naturalWidth;
      const videoHeight = imageElement.naturalHeight;
      const videoAspectRatio = videoWidth / videoHeight;

      // Calculate the aspect ratio of the displayed video bounds
      const displayAspectRatio = imageBounds.width / imageBounds.height;

      // Adjust videoBounds to match the actual video content
      let adjustedVideoBounds: { x: number, y: number, width: number, height: number } = { ...imageBounds };

      if (displayAspectRatio > videoAspectRatio) {
        // Black bars on the left and right (letterboxing)
        const visibleWidth = imageBounds.height * videoAspectRatio;
        const offsetX = (imageBounds.width - visibleWidth) / 2;

        adjustedVideoBounds = {
          x: imageBounds.x + offsetX,
          y: imageBounds.y,
          width: visibleWidth,
          height: imageBounds.height,
        };

      } else if (displayAspectRatio < videoAspectRatio) {
        // Black bars on the top and bottom (pillarboxing)
        const visibleHeight = imageBounds.width / videoAspectRatio;
        const offsetY = (imageBounds.height - visibleHeight) / 2;

        adjustedVideoBounds = {
          x: imageBounds.x,
          y: imageBounds.y + offsetY,
          width: imageBounds.width,
          height: visibleHeight,
        };
      }

      const clickX = event.pageX - adjustedVideoBounds.x;
      const clickY = event.pageY - adjustedVideoBounds.y;

      const relativeX = clickX / adjustedVideoBounds.width;
      const relativeY = clickY / adjustedVideoBounds.height;

      const finalX = videoWidth * relativeX;
      const finalY = videoHeight * relativeY;

      if (zoomFactor) {
        const url = `${host}${AXIS_API_PATH}?areazoom=${Math.floor(finalX)},${Math.floor(finalY)},${zoomFactor}&imagewidth=${videoWidth}&imageheight=${videoHeight}${CAMERA_INDEX}${cameraIndex}`
        issueCommand(url);
      } else {
        const url = `${host}${AXIS_API_PATH}?center=${Math.floor(finalX)},${Math.floor(finalY)}&imagewidth=${videoWidth}&imageheight=${videoHeight}${CAMERA_INDEX}${cameraIndex}`
        issueCommand(url);
      }
    }
  }, [cameraIndex, host]);

  const handleWheelEvent = React.useCallback((event: React.WheelEvent | undefined, kvsName: string | undefined) => {
    if (allowZoom.current === false) return;

    if (event && event.deltaY) {
      if (event.deltaY < 0) {
        zoomActionPool.current.push({ time: Date.now(), value: 50 })
      } else {
        zoomActionPool.current.push({ time: Date.now(), value: -50 })
      }

      setTimeout(() => {
        const l = last(zoomActionPool.current);
        if (l) {
          if (Date.now() - l.time > 300) {
            allowZoom.current = false;
            let sum = 0;
            zoomActionPool.current.forEach(e => sum += e.value);
            if (sum > 0) handleClickEvent(event, 100 + sum);
            else {
              const url = `${host}${AXIS_API_PATH}?rzoom=${sum}${CAMERA_INDEX}${cameraIndex}`
              issueCommand(url);
            }
            zoomActionPool.current = [];
            currentZoomRef.current += sum;

            setTimeout(() => { allowZoom.current = true }, 500)
          }
        }
      }, 310)
    }
  }, [cameraIndex, handleClickEvent, host])

  function renderMainBody(): JSX.Element {
    if (error !== undefined) {
      return (
        <Box display="flex" flexDirection="column">
          <Box>
            <Typography variant="body2" style={{ color: 'white' }} >{error}</Typography>
          </Box>
          <Box display="flex" width="100%" justifyContent="center" alignItems="center">
            <IconButton onClick={perform} style={{ color: 'white' }} size="large">
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      );
    }

    if (Boolean(mjpegUrl)) {
      return (
        <img
          style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute' }}
          src={mjpegUrl}
          alt="NOT FOUND"
          onClick={handleClickEvent}
          onWheel={(e) => handleWheelEvent(e, kvsName)}
          ref={imageRef}
        />
      );
    }

    return (<CircularProgress />)
  }

  return (
    <div style={wraperStyles}>
      <Box
        width="100%"
        height="100%"
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          bgcolor="black"
          height="100%"
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          {error === undefined ? (
            <>
              <DirectionContainer id="ptz-control-description">
                <ArrowContainer>
                  <Box
                    width="50%"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    display="flex"
                  >
                    <Box
                      height="100%"
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="50%"
                      padding="1rem"
                      position="absolute"
                    >
                      {commandFeedback ?
                        <LedFeedback
                          active={commandFeedback}
                        />
                        : (
                          <Aim />
                        )}
                    </Box>
                  </Box>
                </ArrowContainer>
              </DirectionContainer>
              {!isMobile &&
                <ZoomContainer id="zoom-control-description">
                  {isSuperAdministrator && isSuperAdministrator === true &&
                    <Tooltip placement="top" arrow title="Set current position as home">
                      <Button onClick={() => { setCurrentPositionAsHome() }} variant="contained" data-cy="timeline-ptz-set-home-button" >
                        <AddHomeOutlined />
                      </Button>
                    </Tooltip>
                  }
                  <Tooltip arrow placement="top" title="Go to home position">
                    <Button
                      onClick={() => { home() }}
                      variant="contained"
                    >
                      <HomeOutlined />
                    </Button>
                  </Tooltip>
                </ZoomContainer>
              }
            </>
          ) : null}
          {renderMainBody()}
          <div ref={centerDivRef} style={{ position: 'absolute' }} />
        </Box>
      </Box >
    </div>
  )
}

export default PTZControl;
